import { useState } from "react";
import { Copy, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, errMsg, withVia } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const pngFile = async (card) => {
  const blob = await (await fetch(`${BACKEND}${card.image}`)).blob();
  return new File([blob], `middle-${card.id.slice(0, 8)}.png`, { type: "image/png" });
};

// Makes a stored card (POST /share/card), then shows it with share / download / copy link.
export const useShareCard = () => {
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const make = async (body) => {
    setBusy(true);
    try { const { data } = await api.post("/share/card", body); setCard(data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return { card, busy, make, close: () => setCard(null) };
};

export const ShareCardDialog = ({ card, title, onClose }) => {
  const url = card ? withVia(card.link, "copy") : "";
  const copy = async () => { try { await navigator.clipboard.writeText(url); } catch { window.prompt("Copy this link", url); } toast.success("Link copied. It shows the card as a preview, no account needed"); };
  const share = async () => {
    try {
      const file = await pngFile(card);
      const sent = withVia(card.link, "menu");
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text: sent }); return; }
      if (navigator.share) { await navigator.share({ url: sent }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    copy();
  };
  const download = async () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await pngFile(card));
    a.download = `middle-${card.id.slice(0, 8)}.png`;
    a.click();
  };
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="share-card-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Anyone with the link sees this card and can open it on Middle.</DialogDescription></DialogHeader>
        {card && <img src={`${BACKEND}${card.image}`} alt={title} className="w-full rounded-xl border border-border" data-testid="share-card-image" />}
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full" onClick={share} data-testid="share-card-share-btn"><Share2 size={14} className="mr-1.5" />Share</Button>
          <Button variant="outline" className="rounded-full" onClick={download} data-testid="share-card-download-btn"><Download size={14} className="mr-1.5" />Download image</Button>
          <Button variant="outline" className="rounded-full" onClick={copy} data-testid="share-card-copy-btn"><Copy size={14} className="mr-1.5" />Copy link</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
